export const AccountType = {
  OWNER: 'Owner',
  STAFF: 'User',
  EXSTAFF: 'Ex-staff',
  SUPPORT: 'Support',
  ADMIN: 'Admin',
  DDEXPERT: 'DD-Expert',
  UNSPECIFIED: 'Unspecified',
  CLIENT: 'Client',
} as const;

export type AccountTypeValue = (typeof AccountType)[keyof typeof AccountType];
export type AccountTypeKey = keyof typeof AccountType;

export const PermissionType = {
  ORGANISATIONS: 'organisations',
  USERS: 'users',
  MANAGER: 'managerView',
} as const;

export type PermissionTypeValue =
  (typeof PermissionType)[keyof typeof PermissionType];

/** Wire value is the name (`ALL`, `REFRESH`), matching the API enum. */
export enum TablePermission {
  READ = 'READ',
  WRITE = 'WRITE',
  ALL = 'ALL',
  NONE = 'NONE',
  REFRESH = 'REFRESH',
}

export type TablePermissionValue = `${TablePermission}`;

export const AdvancedUserRightsCodes = {
  CANCEL_SUBMISSION: 'CANCEL_SUBMISSION',
  CREATE_EDIT_RETURN: 'CREATE_EDIT_RETURN',
  VAT_NUMBER_CHECKER: 'VAT_NUMBER_CHECKER',
  CREATE_SUBMISSION: 'CREATE_SUBMISSION',
  CREATE_TAX_RATES: 'CREATE_TAX_RATES',
  APPROVE_SUBMISSION: 'APPROVE_SUBMISSION',
  GENERATE_RECONCILIATION: 'GENERATE_RECONCILIATION',
  REFRESH_ROWS_DATA: 'REFRESH_ROWS_DATA',
  GET_ROWS_DATA: 'GET_ROWS_DATA',
  POST_ROWS_DATA: 'POST_ROWS_DATA',
} as const;

export type AdvancedUserRightsCode =
  (typeof AdvancedUserRightsCodes)[keyof typeof AdvancedUserRightsCodes];

export type SharedUser = {
  canManageOrgs: boolean;
  canManageUsers: boolean;
  managerViewEnabled: boolean;
  accountActivated: boolean;
  lastLogin: string;
  accountCreatorName: string | null;
  email: string;
  firstName: string;
  lastName: string;
  userUuid: string;
  accountUuid: string;
  accountType: string;
  isLicenseOwner: boolean;
  profilePicture?: string;
  hasProfilePicture?: boolean;
  mfa?: boolean;
  hasPassword?: boolean;
  companyName?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  website?: string;
  timezone?: string;
  xeroEnabled?: boolean;
  quickbooksEnabled?: boolean;
  fullName?: string;
};

export type LoginDetails = {
  userUuid: string;
  loginTimestamp: Date | string | number;
  loginIP: string;
};

export type UserRights = {
  uuid: string;
  accountUuid: string;
  organisationId: string;
  tablePermission: string;
  advancedRights: string[];
  legalName: string;
  friendlyName: string;
  providerName: string;
  userUuid?: string;
};

export const OperationAppName = {
  ROWS: 'ROWS',
  GENERATE_TAX: 'GENERATE_TAX',
} as const;

export type OperationAppNameValue =
  (typeof OperationAppName)[keyof typeof OperationAppName];

export type AdvancedUserRights = {
  uuid: string;
  name: string;
  description: string;
  shortName: string;
  sortOrder: number;
  operationRightCode: string;
  appName?: string | null;
};

/**
 * Catalogue of operations. generate.TAX loads GET /auth/permissions/get/operations.
 * Rows calls GET /permissions/staff/:accountId/connections/:organisationId/operations only from the temporary Test button on each Users access-rights row.
 * Rows: only `appName === 'ROWS'` — missing/null/other values are dropped.
 * generate.TAX: `GENERATE_TAX`, plus null/undefined (legacy rows without the field).
 */
export function filterOperationsByAppName<
  T extends { appName?: string | null },
>(operations: T[] | undefined | null, app: OperationAppNameValue): T[] {
  const list = operations ?? [];
  if (app === OperationAppName.ROWS) {
    return list.filter((op) => op.appName === OperationAppName.ROWS);
  }
  return list.filter(
    (op) => op.appName === OperationAppName.GENERATE_TAX || op.appName == null,
  );
}

export type NewMember = {
  firstName: string;
  lastName: string;
  email: string;
  isClient: boolean;
};

export const resolveAccountType = (
  accountTypeKey: string | undefined,
): AccountTypeValue | undefined => {
  if (!accountTypeKey) return undefined;
  const fromKey = AccountType[accountTypeKey as AccountTypeKey];
  if (fromKey) return fromKey;
  const normalised = accountTypeKey.trim().toLowerCase();
  return (Object.values(AccountType) as AccountTypeValue[]).find(
    (value) => value.toLowerCase() === normalised,
  );
};

export const ACCOUNT_TYPE_LABEL_KEYS: Record<string, string> = {
  OWNER: 'users.account-type-owner',
  STAFF: 'users.account-type-user',
  CLIENT: 'users.account-type-client',
  EXSTAFF: 'users.account-type-ex-staff',
  SUPPORT: 'users.account-type-support',
  ADMIN: 'users.account-type-admin',
  DDEXPERT: 'users.account-type-dd-expert',
  UNSPECIFIED: 'users.account-type-unspecified',
};
